<script setup lang="ts">
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import AutoReserveTermsAndConditions from './components/AutoReserveTermsAndConditions.vue';
import { type TripSearchForm } from '@search/trip/TripSearchForm';

const emit = defineEmits<{
    close: [args?: { silentClose?: boolean }];
}>();
const progressbar = ref<HTMLElement>();
const rateLoading = ref(false);
const autoReserveStore = useAutoReserveStore();
const { autoReserveSuccessRate: percent } = storeToRefs(autoReserveStore);
const { width, height } = useElementSize(progressbar);
const progressbarInformation = ref<HTMLElement>();
const { width: informationWidth } = useElementSize(progressbarInformation);
const progress = ref(0);
const authStore = useAuthStore();
const analyticsStore = useAnalyticsStore();
const router = useRouter();
const searchFormStore = useSearchFormStore();
const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
const route = useRoute();
const { reserveType } = storeToRefs(searchFormStore);
const { destination, origin, departureDate } = moduleToRefs(
    currentSearchForm.value
);
const { open } = useModal({
    component: AutoReserveTermsAndConditions,
    modalAttrs: {
        zIndexFn: () => 99999999999,
    },
});
const { login } = useAuth();
const reserveName = computed(() =>
    reserveType.value === 'flight' ? 'پرواز' : 'اتوبوس'
);
const tripRouteTitle = computed(
    () =>
        ` ${reserveName.value} ${origin.value?.title} به ${destination.value?.title}`
);
const sections = [
    { label: 'کم', color: 'var(--yellow-400)' },
    { label: 'قابل قبول', color: 'var(--green-400)' },
    { label: 'زیاد', color: 'var(--green-600)', textColor: 'white' },
];
const informationColor = computed(() => {
    const sectionWidth = width.value / sections.length;
    return (
        sections.find((_, index) => progress.value < sectionWidth * (index + 1))
            ?.color || 'var(--gray-400)'
    );
});
const details = [
    'مشخصات بلیط مورد نظرت رو وارد کن',
    'کیف پول مستربلیط رو به مقدار مورد نیاز شارژ کن',
    'در لحظه موجود شدن، بلیط برای شما صادر میشه',
];
const closeModal = (silent?: boolean) => {
    emit('close', { silentClose: silent });
};
const proceed = async () => {
    const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
    analyticsStore.trackAll('auto-reserve-modal-button-click', {
        loggedIn: authStore.loggedIn,
        from: currentSearchForm.value?.origin?.title,
        to: currentSearchForm.value?.destination?.title,
        adultCount: currentSearchForm.value?.adultCount,
        creationSource: authStore.siteSourceId,
        reserveType: reserveType.value,
        tripType: currentSearchForm.value?.tripType,
        link: route.fullPath,
    });
    const loggedIn = await login({
        message: 'برای استفاده از رزرو خودکار باید ابتدا وارد شوید.',
    });
    if (loggedIn) {
        closeModal(true);
        analyticsStore.trackAll('auto-reserve-proceed-to-cart', {
            from: currentSearchForm.value?.origin?.title,
            to: currentSearchForm.value?.destination?.title,
            adultCount: currentSearchForm.value?.adultCount,
            creationSource: authStore.siteSourceId,
            reserveType: reserveType.value,
            tripType: currentSearchForm.value?.tripType,
            link: route.fullPath,
        });
        router.push('/reserve/auto-reserve');
    }
};
const updateAccordion = (value?: boolean) => {
    if (value) {
        analyticsStore.trackAll('AutoNewModal_accordion_clicked');
    }
};

const availability = computed(() => {
    switch (true) {
        case percent.value > 0 && percent.value <= 33.3:
            return 'کم است.';
        case percent.value > 33.3 && percent.value <= 66.6:
            return 'وجود دارد.';
        case percent.value > 66.6 && percent.value <= 100:
            return 'زیاد است.';
        default:
            return '';
    }
});

const informationLocation = computed(
    () =>
        `${((width.value - informationWidth.value - 10) * percent.value) / 100}px`
);

watch(
    [height, width, percent],
    ([, width, percent]) => {
        progress.value = (width * percent) / 100;
    },
    { immediate: true }
);

onMounted(() => {
    rateLoading.value = true;
    autoReserveStore.loadAutoReserveRate();
    rateLoading.value = false;
});
</script>
<template>
    <mr-modal
        title="سرویس رزرو خودکار"
        width="500px"
        mobile-page
        hide-header
        class="modal"
        @close="closeModal(false)"
    >
        <template #default>
            <section class="modal-header">
                <mr-button
                    class="back-btn"
                    :icon-button="{
                        icon: faArrowRight,
                        color: 'var(--cool-gray-900)',
                    }"
                    size="medium"
                    variant="transparent"
                    @click="closeModal(false)"
                />
                <mr-typography weight="700" color="var(--cool-gray-800)">
                    فعالسازی رزرو خودکار
                </mr-typography>
            </section>
            <section class="details-section">
                <div class="trip-detail">
                    <mr-typography color="var(--cool-gray-800)">
                        {{ tripRouteTitle }}
                    </mr-typography>
                    <mr-typography weight="600" color="var(--cool-gray-800)">
                        {{ dayConfig.js(departureDate).format('dddd DD MMMM') }}
                    </mr-typography>
                </div>
                <div class="auto-reserve-info">
                    <mr-typography weight="700">
                        جستجو و خرید این بلیط رو به رزرو خودکار بسپار!
                    </mr-typography>
                    <mr-typography size="small">
                        رزرو خودکار به طور مداوم به جای شما بلیط‌ها را بررسی
                        کرده و به محض پیدا شدن بلیط دلخواه، خرید را انجام خواهد
                        داد.
                    </mr-typography>
                    <mr-accordion
                        class="accordion-list"
                        @update:model-value="updateAccordion"
                    >
                        <template #title-prepend>
                            <div class="question-mark">
                                <mr-typography
                                    weight="900"
                                    color="var(--blue-400)"
                                >
                                    ؟
                                </mr-typography>
                            </div>
                        </template>
                        <template #title>
                            <mr-typography
                                size="small"
                                weight="500"
                                color="var(--cool-gray-800)"
                            >
                                رزرو خودکار چطور کار می‌کند؟
                            </mr-typography>
                        </template>
                        <div class="details">
                            <div
                                v-for="(detail, index) in details"
                                :key="detail"
                                class="detail"
                            >
                                <mr-typography
                                    class="indicator"
                                    weight="900"
                                    color="var(--cool-gray-800)"
                                >
                                    {{ index + 1 }}
                                </mr-typography>
                                <mr-typography
                                    size="small"
                                    weight="500"
                                    color="var(--cool-gray-800)"
                                >
                                    {{ detail }}
                                </mr-typography>
                            </div>
                            <div class="detail">
                                <mr-typography
                                    class="indicator warning"
                                    weight="900"
                                    color="var(--yellow-500)"
                                >
                                    !
                                </mr-typography>
                                <mr-typography
                                    size="small"
                                    weight="500"
                                    color="var(--cool-gray-800)"
                                >
                                    بلیط پیدا نشد، کل هزینه برگشت داده می‌شه!
                                </mr-typography>
                            </div>
                        </div>
                    </mr-accordion>
                </div>
                <div class="chances">
                    <mr-loading v-if="rateLoading" />
                    <div v-else ref="progressbar" class="progressbar">
                        <div
                            v-for="{ color, label, textColor } in sections"
                            :key="label"
                            :style="{ backgroundColor: color }"
                            class="indicator"
                        >
                            <mr-typography
                                :color="textColor"
                                size="small"
                                weight="600"
                            >
                                {{ label }}
                            </mr-typography>
                        </div>
                        <div
                            ref="progressbarInformation"
                            class="progress info"
                            :style="{
                                borderColor: informationColor,
                                right: informationLocation,
                                bottom: `${height + 12}px`,
                            }"
                        >
                            <mr-typography size="x-small" weight="700">
                                احتمال پیدا شدن این بلیط
                            </mr-typography>
                        </div>
                        <div
                            class="progress"
                            :style="{
                                color: informationColor,
                                right: `${progress}px`,
                                bottom: `${height - 6}px`,
                            }"
                        >
                            |
                        </div>
                    </div>
                    <mr-typography
                        size="x-small"
                        weight="500"
                        color="var(--gray-700)"
                    >
                        بر اساس سوابق ظرفیت‌ پروازها، آمار فروش بلیط در این مسیر
                        و تعداد درخواست رزرو سایر کاربران، احتمال پیدا شدن این
                        بلیط برای شما
                        <mr-typography
                            type="span"
                            size="x-small"
                            color="var(--gray-700)"
                            weight="700"
                        >
                            {{ availability }}
                        </mr-typography>
                    </mr-typography>
                </div>
            </section>
        </template>

        <template #footer>
            <div class="footer-buttons">
                <mr-typography size="x-small">
                    ادامه فرآیند به منزله پذیرش
                    <mr-typography
                        size="x-small"
                        color="var(--blue-400)"
                        class="terms-and-conditions"
                        @click="open()"
                    >
                        شرایط و قوانین رزرو خودکار
                    </mr-typography>
                    است.
                </mr-typography>
                <mr-button full-width @click.stop="proceed()">
                    ادامه و ثبت درخواست رزرو خودکار
                </mr-button>
            </div>
        </template>
    </mr-modal>
</template>

<style lang="scss" scoped>
.modal {
    :deep(.main-slot-wrapper) {
        background-color: usePalette($p-cool-gray, $p-100) !important;
        padding: 0 !important;
    }
}
.modal-header {
    background-color: white !important;
    display: grid;
    grid-template-columns: 0.2fr 1fr 0.2fr;
    gap: 10px;
    justify-items: center;
    align-items: center;
    padding-block: 10px;
}

.details-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background-color: usePalette($p-cool-gray, $p-100);
    padding: 12px 16px;
    .trip-detail {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background-color: white;
        padding: 12px;
        border-radius: 12px;
    }

    .auto-reserve-info {
        display: flex;
        flex-direction: column;
        padding: 12px;
        border-radius: 12px;
        background-color: white;
        align-items: center;
        gap: 12px;
        .accordion-list {
            border: 1px solid usePalette($p-cool-gray, $p-200);
            border-radius: 8px;
            width: 100%;
            :deep(.header) {
                display: flex;
                align-items: center;
                padding: 8px;
                .question-mark {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 24px;
                    height: 24px;
                    border-radius: 9999px;
                    border: 1px solid usePalette($p-blue, $p-400);
                    background-color: usePalette($p-blue, $p-100);
                }
            }
            .details {
                display: flex;
                flex-direction: column;
                gap: 12px;
                padding: 12px;
                .detail {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    .indicator {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 24px;
                        height: 24px;
                        border-radius: 8px;
                        background-color: usePalette($p-blue, $p-100);
                        &.warning {
                            background-color: usePalette($p-yellow, $p-100);
                        }
                    }
                }
            }
        }
    }

    .chances {
        display: flex;
        flex-direction: column;
        gap: 16px;
        background-color: white;
        padding: 12px;
        border-radius: 12px;
        .progressbar {
            display: flex;
            align-items: center;
            position: relative;
            padding-top: 50px;
            .progress {
                transition: right 1s ease-out;
                position: absolute;
                &.info {
                    border: 1px solid usePalette($p-gray, $p-400);
                    padding: 8px;
                    border-radius: 5px;
                }
            }
            .indicator {
                padding-block: 10px;
                display: flex;
                justify-content: center;
                align-items: center;
                flex: 1 1 0;
            }
            .indicator:first-child {
                border-top-right-radius: 8px;
                border-bottom-right-radius: 8px;
            }
            .indicator:nth-child(3) {
                border-top-left-radius: 8px;
                border-bottom-left-radius: 8px;
            }
        }
    }
}

.footer-buttons {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
    .terms-and-conditions {
        display: inline;
        cursor: pointer;
    }
}
</style>
